import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DownloadCloud, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Section, Progress } from "@/components/shared";

const ROLE_LABEL = { main: "Main category", sub: "Sub-category", product: "Product", colour: "Colour", asset: "Asset category", ignore: "Ignore" };
const abs = (u) => (u || null);

export function DriveImportWizard({ clientId }) {
  const [step, setStep] = useState("link");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(null);
  const [mapping, setMapping] = useState({});
  const [names, setNames] = useState(null);
  const [nameMap, setNameMap] = useState({});
  const [preview, setPreview] = useState(null);
  const [job, setJob] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    api.get(`/clients/${clientId}/drive-import`).then((r) => { if (r.data && r.data.status && r.data.status !== "idle") setJob(r.data); }).catch(() => {});
  }, [clientId]);
  useEffect(() => {
    if (!job || !["queued", "running"].includes(job.status)) return undefined;
    const t = window.setTimeout(async () => {
      try { const { data } = await api.get(`/clients/${clientId}/drive-import`); setJob(data); } catch { /* ignore */ }
    }, 1800);
    return () => window.clearTimeout(t);
  }, [job, clientId]);

  const doScan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/drive-import/scan`, { link });
      setScan(data); setMapping(data.mapping || {}); setStep("mapping");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const afterMapping = async () => {
    if (!Object.values(mapping).includes("asset")) { await doPreview(); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/drive-import/asset-names`, { link, mapping });
      setNames(data); setNameMap(data.name_map || {}); setStep("names");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/drive-import/preview`, { link, mapping, names: nameMap });
      setPreview(data); setStep("preview");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const doConfirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/drive-import/confirm`, { link, mapping, names: nameMap });
      setJob(data); setDismissed(false); setStep("link"); setScan(null); setNames(null); setPreview(null);
      toast.success("Import started");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const doResume = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/drive-import/resume`);
      setJob(data); setDismissed(false); toast.success("Resuming import");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/drive-import/cancel`);
      setJob({ status: "idle" }); toast.success("Import cancelled");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const running = job && ["queued", "running"].includes(job.status);
  const pct = job?.total ? Math.round((job.processed / job.total) * 100) : 0;
  const cleanDone = job && job.status === "completed" && (job.failed || 0) === 0;
  const seenKey = job?.id ? `drive-import-celebrated-${job.id}` : null;
  const [alreadySeen, setAlreadySeen] = useState(false);
  useEffect(() => { setAlreadySeen(seenKey ? !!window.localStorage.getItem(seenKey) : false); }, [seenKey]);
  const celebrate = Boolean(cleanDone && !dismissed && !alreadySeen);
  useEffect(() => {
    if (!celebrate || !seenKey) return undefined;
    return () => { window.localStorage.setItem(seenKey, "1"); };
  }, [celebrate, seenKey]);

  return (
    <Section title="Import from Drive" testId="drive-import-section">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 shrink-0 rounded-md bg-indigo-600 text-white grid place-items-center"><DownloadCloud size={18} /></div>
        <div className="flex-1 space-y-4">
          <p className="text-sm text-slate-600">Mirror a client's existing Google Drive folder into the portal's structure. The source is only read and copied — never moved, renamed or deleted.</p>

          {step === "link" && (
            <div className="space-y-2">
              <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste a Google Drive folder link (shared with admin@sugarcubes.co.in)" data-testid="drive-import-link-input" />
              <Button className="bg-slate-900 hover:bg-slate-800" disabled={busy || !link.trim()} onClick={doScan} data-testid="drive-import-scan-btn">{busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <ArrowRight size={15} className="mr-1.5" />}Scan folder</Button>
            </div>
          )}

          {step === "mapping" && scan && (
            <div className="space-y-3" data-testid="drive-import-mapping">
              <div className="text-sm font-medium text-slate-800">{scan.folder_name}</div>
              <div className="space-y-2">
                {scan.levels.map((lvl) => (
                  <div key={lvl.level} className="grid grid-cols-[1fr_200px] items-center gap-3 border border-slate-200 p-2">
                    <div className="text-xs text-slate-600"><span className="font-semibold">Level {lvl.level}</span> · {lvl.count} folders<div className="text-slate-400 truncate">e.g. {lvl.examples.join(", ")}</div></div>
                    <Select value={mapping[String(lvl.level)] || "ignore"} onValueChange={(v) => setMapping((m) => ({ ...m, [String(lvl.level)]: v }))}>
                      <SelectTrigger className="h-8 bg-white text-xs" data-testid={`drive-import-role-${lvl.level}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{(scan.roles || []).map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setStep("link")}>Back</Button>
                <Button className="bg-slate-900 hover:bg-slate-800" size="sm" disabled={busy} onClick={afterMapping} data-testid="drive-import-mapping-next-btn">{busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null}Next</Button>
              </div>
            </div>
          )}

          {step === "names" && names && (
            <div className="space-y-3" data-testid="drive-import-names">
              <div className="text-sm font-medium text-slate-800">Asset folder names</div>
              <p className="text-xs text-slate-500">Each source folder found at the asset level — pick where its files go. Several names can point to the same category. Ignore skips that folder entirely.</p>
              <div className="space-y-2">
                {names.names.map((n) => (
                  <div key={n.name} className="grid grid-cols-[1fr_200px] items-center gap-3 border border-slate-200 p-2">
                    <div className="text-xs text-slate-600 truncate"><span className="font-semibold">{n.name}</span><div className="text-slate-400">{n.count} files</div></div>
                    <Select value={nameMap[n.name.trim().toLowerCase()] || "uncategorised"} onValueChange={(v) => setNameMap((m) => ({ ...m, [n.name.trim().toLowerCase()]: v }))}>
                      <SelectTrigger className="h-8 bg-white text-xs" data-testid={`drive-import-name-${n.name}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="uncategorised">Uncategorised</SelectItem>
                        <SelectItem value="ignore">Ignore</SelectItem>
                        {names.image_categories.length > 0 && (
                          <SelectGroup>
                            <SelectLabel>Image categories</SelectLabel>
                            {names.image_categories.map((c) => <SelectItem key={c.token} value={c.token}>{c.name}</SelectItem>)}
                          </SelectGroup>
                        )}
                        {names.video_categories.length > 0 && (
                          <SelectGroup>
                            <SelectLabel>Video categories</SelectLabel>
                            {names.video_categories.map((c) => <SelectItem key={c.token} value={c.token}>{c.name}</SelectItem>)}
                          </SelectGroup>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setStep("mapping")}>Back</Button>
                <Button className="bg-slate-900 hover:bg-slate-800" size="sm" disabled={busy} onClick={doPreview} data-testid="drive-import-preview-btn">{busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null}Build preview</Button>
              </div>
            </div>
          )}

          {step === "preview" && preview && (
            <div className="space-y-4" data-testid="drive-import-preview">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border border-slate-200 p-2"><div className="text-xl font-bold">{preview.products}</div><div className="text-[11px] text-slate-500">products</div></div>
                <div className="border border-slate-200 p-2"><div className="text-xl font-bold">{preview.total_files}</div><div className="text-[11px] text-slate-500">files to copy</div></div>
                <div className="border border-slate-200 p-2"><div className="text-xl font-bold">{preview.uncategorised}</div><div className="text-[11px] text-slate-500">→ Uncategorised</div></div>
              </div>
              <div className="flex flex-wrap gap-1.5 text-[11px]">{preview.category_counts.map((c) => <span key={c.name} className="border border-slate-200 px-2 py-0.5">{c.name} · {c.count}</span>)}</div>
              {(preview.new_mains.length + preview.new_subs.length + preview.new_products.length) > 0 && (
                <div className="text-xs text-slate-600" data-testid="drive-import-new-items">
                  <div className="font-semibold mb-1">Will be created</div>
                  {preview.new_mains.length > 0 && <div>Main: {preview.new_mains.join(", ")}</div>}
                  {preview.new_subs.length > 0 && <div>Sub: {preview.new_subs.join(", ")}</div>}
                  {preview.new_products.length > 0 && <div>{preview.new_products.length} products</div>}
                </div>
              )}
              <div className="max-h-[420px] overflow-y-auto space-y-3">
                {preview.product_rows.map((p, i) => (
                  <div key={i} className="border border-slate-200 p-2" data-testid={`drive-import-product-${i}`}>
                    <div className="text-sm font-medium text-slate-800">{p.code ? <span className="text-slate-400 mr-1">{p.code}</span> : null}{p.name}</div>
                    <div className="text-[11px] text-slate-400 mb-1.5">{[p.main, p.sub].filter(Boolean).join(" / ")}</div>
                    {p.groups.map((g, gi) => (
                      <div key={gi} className="mb-1.5">
                        <div className="text-[11px] font-semibold text-slate-500">{g.colour !== "Default" ? `${g.colour} · ` : ""}{g.category} · {g.count}</div>
                        <div className="flex gap-1 mt-1">
                          {g.thumbs.map((t) => <img key={t.id} src={abs(t.thumb)} alt={t.name} loading="lazy" className="h-12 w-12 object-cover border border-slate-200" />)}
                          {g.more > 0 && <div className="h-12 w-12 grid place-items-center border border-slate-200 text-[11px] text-slate-500">+{g.more}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setStep(names ? "names" : "mapping")}>Back</Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm" disabled={busy} onClick={doConfirm} data-testid="drive-import-confirm-btn"><CheckCircle2 size={14} className="mr-1.5" />Confirm &amp; copy</Button>
              </div>
            </div>
          )}

          {job && job.status && job.status !== "idle" && step === "link" && (
            <div className="border-t border-slate-100 pt-3" data-testid="drive-import-progress">
              {celebrate ? (
                <div className="drive-done-pop flex flex-col items-center text-center py-4" data-testid="drive-import-celebration">
                  <svg viewBox="0 0 52 52" className="h-14 w-14 mb-2">
                    <circle cx="26" cy="26" r="24" fill="none" stroke="#10b981" strokeWidth="2" opacity="0.25" />
                    <path className="drive-done-check" d="M14 27 l8 8 l16 -18" fill="none" stroke="#10b981" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="text-base font-semibold text-slate-900">You're all set.</div>
                  <div className="text-xs text-slate-400 italic">Organised. Clean. One system.</div>
                  <div className="text-xs text-slate-600 mt-2">{job.processed || 0} products · {job.copied || 0} files copied</div>
                  <Button variant="ghost" size="sm" className="mt-2" onClick={() => setDismissed(true)} data-testid="drive-import-celebration-dismiss">Dismiss</Button>
                </div>
              ) : (
                <div className="text-xs text-slate-600">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-medium ${job.stalled ? "text-amber-600" : "capitalize"}`}>Import · {job.stalled ? "Stalled" : job.status}</span>
                    {job.total ? <span>{job.processed}/{job.total} products</span> : null}
                  </div>
                  {running && !job.stalled && <Progress value={pct} />}
                  {(job.copied || job.skipped || job.failed) ? <div className="mt-1">{job.copied || 0} copied · {job.skipped || 0} skipped · {job.failed || 0} failed</div> : null}
                  {job.stalled && <div className="mt-1 text-amber-700">No progress for a few minutes — resume or cancel.</div>}
                  {job.status === "completed" && !job.stalled && <div className="mt-1">Done · {job.copied || 0} copied · {job.skipped || 0} skipped · {job.failed || 0} failed</div>}
                  {job.error && <div className="mt-1 text-rose-700">{job.error}</div>}
                  {(job.stalled || job.status === "failed") && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={doResume} data-testid="drive-import-resume-btn">{busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}Resume</Button>
                      <Button size="sm" variant="outline" disabled={busy} onClick={doCancel} data-testid="drive-import-cancel-btn">Cancel</Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}
